import { SPLASH_SCREEN_ROUTING } from '@/views/business/settings/splash-screens/const'
import { BOOKSY_PAY_DISTRIBUTION_MODELS, SOFT_OPT_OUT_DISTRIBUTION_MODELS } from '@/composables/payments/booksy-pay/consts'
import type { BooksyPayDistributionModel } from '@/composables/payments/booksy-pay/consts'

import {
  BOOKSY_PAY_ENTRY_TARGETS,
  type BooksyPayEntryTarget,
} from './consts'

export type { BooksyPayEntryTarget }
export { BOOKSY_PAY_ENTRY_TARGETS }

const softOptOutDistributionModels = [
  BOOKSY_PAY_DISTRIBUTION_MODELS.SOFT_OPT_OUT_A,
  BOOKSY_PAY_DISTRIBUTION_MODELS.SOFT_OPT_OUT_B,
]

type BooksyPayStore = {
  state: {
    business: {
      booksyPaySettings: unknown
    }
  }
  getters: {
    'identity/isOwner': boolean
    'business/isBooksyPayEnabled': boolean
    'business/isBooksyPayAllowed': boolean
    'business/booksyPayDistributionModel': BooksyPayDistributionModel | null
  }
  dispatch: (action: string) => Promise<unknown>
}

type BooksyPayRoute = {
  params?: Record<string, unknown>
  query?: Record<string, unknown>
}

type EntryContext = {
  store: BooksyPayStore
  route: BooksyPayRoute
}

const ensureBooksyPaySettingsLoaded = async (store: BooksyPayStore): Promise<void> => {
  if (!store.state.business.booksyPaySettings) {
    await store.dispatch('business/loadBooksyPaySettings')
  }
}

const getOnboardingRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  name: 'settings.payments.booksy-pay.onboarding',
  params: {
    businessId: route?.params?.businessId,
  },
  query: route?.query,
})

const getActivationRoute = ({ route }: EntryContext): Record<string, unknown> => {
  const activationRoute = SPLASH_SCREEN_ROUTING.ENABLE_FEATURE.BOOKSY_PAY_ACTIVATION

  return {
    ...activationRoute,
    params: {
      ...activationRoute.params,
      businessId: route?.params?.businessId,
    },
    ...(route?.query ? { query: route.query } : {}),
  }
}

const getOptOutLandingPageRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  name: 'settings.payments.booksy-pay.opt-out-landing-page',
  params: {
    businessId: route?.params?.businessId,
  },
  query: route?.query,
})

const getEntryRedirectRoute = ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Record<string, unknown> | null => {
  const isOwner = store.getters['identity/isOwner']
  const isBooksyPayEnabled = store.getters['business/isBooksyPayEnabled']
  const distributionModel = store.getters['business/booksyPayDistributionModel']

  if (!isOwner) {
    return { name: 'calendar' }
  }

  if (targetView === BOOKSY_PAY_ENTRY_TARGETS.ACTIVATION) {
    if (isBooksyPayEnabled) {
      return getOnboardingRoute({ store, route })
    } else if (SOFT_OPT_OUT_DISTRIBUTION_MODELS.some((model) => model === distributionModel)) {
      return getOptOutLandingPageRoute({ store, route })
    }
  }

  if (targetView === BOOKSY_PAY_ENTRY_TARGETS.ONBOARDING && !isBooksyPayEnabled) {
    return getActivationRoute({ store, route })
  }

  if (
    targetView === BOOKSY_PAY_ENTRY_TARGETS.OPT_OUT_LANDING_PAGE
    && !isBooksyPayEnabled
    && !SOFT_OPT_OUT_DISTRIBUTION_MODELS.some((model) => model === distributionModel)
  ) {
    return getOnboardingRoute({ store, route })
  }

  return null
}

export const resolveBooksyPayEntryRedirect = async ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Promise<Record<string, unknown> | null> => {
  await ensureBooksyPaySettingsLoaded(store)
  return getEntryRedirectRoute({ targetView, store, route })
}
